import axiosInstance from './axiosInstance';

import type { Response } from './axiosInstance'

export type jobEnity = {
    id: number;
    // jobUid is the platform's own identifier, unique across clusters. Prefer it over
    // `id`, which is the scheduler's job id and repeats between clusters.
    jobUid?: string;
    cluster?: string;
    jobName: string;
    cpuCores: string;
    // The scheduler's id. The ledger-backed history reports it as a string because it
    // arrives from the remote cluster as text; the accounting database reports a number.
    idJob: number | string;
    partition: string;
    nodeList: string;
    timeSubmit: number;
    timeStart: number;
    timeEnd: number;
    state: number;
    workDir: string;
    // Ledger-backed rows carry how the result retrieval went. `failed` means the job finished
    // but its files could not be brought home; stagingMessage says why.
    stagingState?: string;
    stagingMessage?: string;
    lastError?: string;
    // Where the job's stdout was placed locally after a remote pull.
    outputPath?: string;
    idUser: {
        Int64: number;
        Valid: boolean;
    };
    timelimit: number;
};

export enum jobStateEnum {
    PENDING = 0,
    RUNNING,
    SUSPENDED,
    COMPLETE,
    CANCELED,
    FAILED,
    TIMEOUT,
    NODE_FAIL,
    PREEMPTED,
    BOOT_FAIL,
    DEADLINE,
    OOM,
    END
}

export interface SubmitJobParams {
    // cluster selects the destination. Omit it to use the default cluster, which is the
    // local one, so existing call sites keep working unchanged.
    cluster?: string;
    // remote_user selects which of the user's accounts on a remote cluster to submit
    // under. A user may hold several (different groups, different quotas); omitting it
    // uses the account marked default.
    remote_user?: string;
    path: string;
    cpu_num: number;
    gpu_num: number;
    memory: number;
    jobName: string;
    max_time?: number;
    partition?: string;
    node?: string;
    comment?: string;
}

export const submitJob = async (params: SubmitJobParams): Promise<Response<boolean> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<boolean> | undefined>('/job/submit', {
            params: {
                path: params.path,
                cpu_num: params.cpu_num,
                gpu_num: params.gpu_num,
                memory: params.memory,
                jobName: params.jobName,
                partition: params.partition,
                node: params.node,
                comment: params.comment,
                max_time: params.max_time,
                cluster: params.cluster,
                remote_user: params.remote_user
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
};

export const cancelJob = async (jobId: number | string, cluster?: string): Promise<Response<boolean> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<boolean> | undefined>('/job/cancel', {
            params: {
                jobId,
                cluster
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
};

interface GetJobsParams {
    page: number;
    pageSize: number;
    // Omit for the default cluster; pass 'all' to merge every cluster the user can see.
    cluster?: string;
    jobName?: string;
    startTime?: string;
    endTime?: string;
    state?: string;
}

export const getJobs = async (params: GetJobsParams): Promise<Response<{ jobs: jobEnity[], total: number }> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<{ jobs: jobEnity[], total: number }> | undefined>('/job/list', {
            params: {
                page: params.page,
                page_size: params.pageSize,
                job_name: params.jobName,
                start_time: params.startTime,
                end_time: params.endTime,
                state: params.state,
                cluster: params.cluster
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
};

export type ClusterNode = {
    name: string;
    partition: string;
    state: string;
    cpus_alloc: number;
    cpus_idle: number;
    cpus_total: number;
    memory_mb: number;
    reason?: string;
    available: boolean;
}

export type ClusterOverview = {
    nodes: ClusterNode[];
    total_cpus: number;
    idle_cpus: number;
    pending_jobs: number;
    running_jobs: number;
    unavailable_nodes?: string[];
}

// ok:false means the cluster could NOT be read — never an empty cluster. See ClusterOverviewPanel.
export const getClusterOverview = async (cluster?: string): Promise<Response<ClusterOverview> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<ClusterOverview>>('/job/clusterOverview', { params: { cluster } });
        return response.data;
    } catch (e) {
        return undefined;
    }
};

export const getMachineList = async (cluster?: string): Promise<Response<{ [partition: string]: string[] }> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<{ [partition: string]: string[] }> | undefined>('/job/machineList', { params: { cluster } });
        return response.data;
    } catch (e) {
        return undefined;
    }
};

export type QueryJobInBatchItem = {
    time: number;
    workDir: string;
}

export type QueryJobInBatchResponseItem = {
    time: number;
    workDir: string;
    jobId: number;
    state: jobStateEnum
}

export const queryJobInBatch = async (list: QueryJobInBatchItem[]): Promise<Response<QueryJobInBatchResponseItem[]> | undefined> => {
    try {
        const response = await axiosInstance.post<Response<QueryJobInBatchResponseItem[]> | undefined>('/job/batch', list, {
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
}

type QueryUserResourceResponse = {
    user: string,
    partition: string,
    node: string,
    max_walltime: string,
    max_cpus: number,
    max_gpus: number
}

export const queryUserResource = async (params: { partition?: string, node?: string, cluster?: string }): Promise<Response<QueryUserResourceResponse> | undefined> => {
    try {
        const response = await axiosInstance.get<Response<QueryUserResourceResponse> | undefined>('/job/userResource', {
            params,
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return response.data;
    } catch (e) {
        return undefined;
    }
}
